import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createTemplateSchema, type CreateTemplateFormValues } from '../schemas/documentTemplate.schema';
import { applyServerFieldErrors } from '../utils/formErrors';
import { createOwnerPayload } from '../utils/templateOwnership';
import { useHasInScopeDealers } from './useInScopeDealers';
import { useTemplateOwnerContext } from './useTemplateOwnerContext';
import { useCreateTemplateMutation } from './useTemplateMutations';
import { useKeyFromName } from './useKeyFromName';
import { templateTabPath } from '../utils/templateTabs';

export function useCreateTemplateForm() {
  const navigate = useNavigate();
  const mutation = useCreateTemplateMutation();
  const { isInternal, homeDealerId } = useTemplateOwnerContext();
  const { hasDealers, isLoading: isCheckingDealers } = useHasInScopeDealers(!isInternal);
  const form = useForm<CreateTemplateFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(createTemplateSchema),
    defaultValues: {
      key: '', name: '', description: '', documentType: undefined,
      ownerKind: isInternal ? 'platform' : 'dealer',
      ownerDealerId: isInternal ? null : homeDealerId,
    },
  });

  useKeyFromName(form);

  const submit = form.handleSubmit((values) => {
    const { ownerKind, ownerDealerId, ...fields } = values;
    const dealerId = createOwnerPayload({
      isInternal,
      homeDealerId,
      choice: ownerKind === 'dealer' && ownerDealerId ? { kind: 'dealer', dealerId: ownerDealerId } : { kind: 'platform' },
    });
    mutation.mutate({ ...fields, dealerId }, {
      onSuccess: (template) => navigate(templateTabPath(template.id, 'draft')),
      onError: (error) => {
        if (!applyServerFieldErrors(error, form.setError, { dealer_id: 'ownerDealerId' })) {
          toast.error((error as Error).message);
        }
      },
    });
  });

  return {
    form,
    submit,
    isSaving: mutation.isPending,
    isInternal,
    hasDealers,
    canSubmit: isInternal || (!isCheckingDealers && hasDealers),
  };
}
